import { __ } from '@wordpress/i18n';
import React from 'react';
import { createRoot } from 'react-dom/client';
import ReactPlayer from 'react-player';
import { disableBodyScroll, clearAllBodyScrollLocks } from 'body-scroll-lock';

const videoComponent = () => {
	const videoComponents = document.querySelectorAll(
		'.component-video:not(.component-lightbox__button):not([data-lightbox-content])'
	);
	let videoLightbox;
	let videoLightboxContent;
	let root;

	const initializeVideoLightbox = (videoLightboxComponent) => {
		const videoLightboxFigure = videoLightboxComponent;

		videoLightboxFigure.classList.add('component-video__button');
		// open video - play icon click
		videoLightboxFigure
			.querySelector('.component-video__play-button')
			.addEventListener('click', openVideoLightbox);
		videoLightboxFigure.addEventListener('click', openVideoLightbox);
		videoLightboxFigure.setAttribute(
			'aria-label',
			__('Open Image in Lightbox', 'propel')
		);
	};

	// open lightbox
	const openVideoLightbox = (e) => {
		e.preventDefault();

		if (!videoLightbox) {
			createVideoLightbox();
		}

		const lightboxVideoComponent =
			e.currentTarget.closest('.component-video');
		const lightboxVideoContent = videoLightbox.querySelector(
			'.component-video-lightbox__content'
		);

		videoLightbox.classList.add('active');

		disableBodyScroll(videoLightbox, {
			reserveScrollBarGap: true,
		});

		setTimeout(function () {
			videoLightbox.classList.add('visible');
		}, 1);

		document.addEventListener('keydown', keydown);

		playVideo(lightboxVideoComponent, lightboxVideoContent);
	};

	const playVideo = (lightboxVideoComponent, lightboxVideoContent) => {
		if (
			!lightboxVideoComponent ||
			!lightboxVideoComponent.hasAttribute('data-embed-url')
		) {
			return;
		}

		if (
			lightboxVideoComponent.classList.contains(
				'component-video-lightbox__video--playing'
			)
		) {
			return;
		}

		stopVideos();

		const videoURL = lightboxVideoComponent.getAttribute('data-embed-url');
		lightboxVideoComponent.classList.add(
			'component-video-lightbox__video--playing'
		);
		root = createRoot(lightboxVideoContent);

		// Check if it's a Wistia video
		const isWistia =
			videoURL.includes('wistia.com') || videoURL.includes('wistia.net');

		root.render(
			<ReactPlayer
				className="component-video-lightbox__video"
				url={videoURL}
				playing
				height={isWistia ? undefined : '100%'}
				width={isWistia ? undefined : '100%'}
				controls={true}
				config={{
					wistia: {
						options: {
							responsive: true,
							videoFoam: true,
						},
					},
				}}
			/>
		);
	};

	const playVideoEvent = (e) => {
		if (
			!e?.detail?.lightboxVideoComponent ||
			!e?.detail?.lightboxVideoContent
		) {
			return;
		}

		const lightboxVideoComponent = e.detail.lightboxVideoComponent;
		const lightboxVideoContent = e.detail.lightboxVideoContent;

		playVideo(lightboxVideoComponent, lightboxVideoContent);
	};

	const stopVideos = () => {
		if (root && root._internalRoot) {
			root.unmount();
		}

		const playingVideos = document.querySelectorAll(
			'.component-video-lightbox__video--playing'
		);

		playingVideos.forEach((playingVideo) => {
			playingVideo.classList.remove(
				'component-video-lightbox__video--playing'
			);
		});
	};

	const closeVideoLightbox = (e) => {
		e.preventDefault();

		videoLightbox.classList.remove('visible');

		stopVideos();

		setTimeout(function () {
			videoLightbox.classList.remove('active');
			clearAllBodyScrollLocks();
			videoLightboxContent.innerHTML = '';
		}, 400);

		root.unmount();

		document.removeEventListener('keydown', closeVideoLightbox);
	};

	const keydown = (e) => {
		if ('Escape' === e.key) {
			closeVideoLightbox(e);
		}
	};

	// create lightbox
	const createVideoLightbox = () => {
		videoLightbox = document.createElement('div');
		videoLightbox.classList.add('bg-dark', 'component-video-lightbox');
		videoLightbox.setAttribute('role', 'dialog');
		videoLightbox.setAttribute('aria-modal', 'true');

		const videoLightboxContainer = document.createElement('div');
		videoLightboxContainer.classList.add(
			'component-video-lightbox__container'
		);

		videoLightboxContent = document.createElement('div');
		videoLightboxContent.classList.add('component-video-lightbox__content');

		const videoLightboxCloseOverlay = document.createElement('button');
		videoLightboxCloseOverlay.classList.add(
			'component-video-lightbox__close-button-overlay'
		);
		videoLightboxCloseOverlay.setAttribute('role', 'button');
		videoLightboxCloseOverlay.setAttribute(
			'aria-label',
			__('Close Lightbox', 'propel')
		);
		videoLightboxCloseOverlay.addEventListener('click', closeVideoLightbox);

		const videoLightboxCloseButton = document.createElement('span');
		videoLightboxCloseButton.classList.add(
			'component-video-lightbox__close-button'
		);
		videoLightboxCloseButton.addEventListener('click', closeVideoLightbox);

		videoLightboxCloseOverlay.appendChild(videoLightboxCloseButton);
		videoLightboxContainer.appendChild(videoLightboxContent);
		videoLightbox.appendChild(videoLightboxContainer);
		videoLightbox.appendChild(videoLightboxCloseOverlay);
		document.body.appendChild(videoLightbox);
	};

	videoComponents.forEach((video) => {
		initializeVideoLightbox(video);
	});

	window.addEventListener('propel-play-video', playVideoEvent);
	window.addEventListener('propel-stop-videos', stopVideos);
};

videoComponent();

export default videoComponent;
